import { Box, Chip, IconButton, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import ConfirmationModal from "components/modals/confirmation-modal";
import { Status } from "contexts/api-key-state";

import { ContentCopy } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import * as QueryKeys from "data";
import { ApiKey } from "data/models";
import { deleteApiKey } from "data/mutations";
import { Gem, Pencil, Trash } from "lucide-react";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { generatePath, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { toTitleCase } from "utils/commons";
import { premiumProducts } from "./api-keys-page";

const renderProductChips = (products: string[]) => {
  let extraProducts: string[] = [];

  return (
    <>
      {products.map((product: string, index: number) => {
        if (index < 2)
          return (
            <Chip
              key={product}
              label={product}
              style={{ marginLeft: "8px" }}
              variant="outlined"
            />
          );
        extraProducts.push(product);
        return <></>;
      })}
      {extraProducts.length > 0 && (
        <Chip
          title={extraProducts.join(", ")}
          label={"+" + extraProducts.length}
          style={{ marginLeft: "8px" }}
          variant="outlined"
        />
      )}
    </>
  );
};

export const useColumns = (identityId: string) => {
  const history = useHistory();

  const queryClient = useQueryClient();
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const apiKeyDeleteMutation = useMutation(
    (selectedKey: string) => deleteApiKey(selectedKey),
    {
      onSuccess: (deletedApiKey) => {
        queryClient.setQueryData<ApiKey[]>(
          [QueryKeys.apiKeys, identityId],
          (oldData) => {
            if (!oldData) return [];
            return oldData.filter(
              (key: ApiKey) => key.name !== deletedApiKey.name
            );
          }
        );

        setDeleteModalIsOpen(false);
      },
    }
  );

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  return [
    { flex: 1, field: "name", headerName: "Name" },
    { flex: 1, field: "creator", headerName: "Creator" },
    {
      flex: 1,
      field: "premium",
      headerName: "Premium",
      renderCell: (params: GridRenderCellParams<ApiKey>) => {
        if (
          params.row.products
            .map((x) => x.productName)
            .some((product: any) => premiumProducts.includes(product))
        ) {
          return (
            <Chip
              size="small"
              icon={<Gem color="#1876d3" width={"20px"} />}
              label={"Premium"}
              variant="outlined"
              color="primary"
            />
          );
        }
      },
    },
    {
      flex: 1,
      field: "key",
      headerName: "Key",
      renderCell: (params: GridRenderCellParams<ApiKey>) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}
        >
          <Typography sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>*************</Typography>
          <IconButton
            size="small"
            sx={{ p: 0, flexShrink: 0 }}
            onClick={(): any => {
              navigator.clipboard.writeText(params.row.consumerKey);
            }}
          >
            <ContentCopy />
          </IconButton>
        </Box>
      ),
    },
    {
      flex: 2,
      field: "products",
      headerName: "Products",

      renderCell: (params: GridRenderCellParams<ApiKey>) => {
        return renderProductChips(
          params.row.products.map((x) => x.productDisplayName)
        );
      },
    },
    {
      flex: 1,
      field: "createdDate",
      headerName: "Create Date",

      renderCell: (params: GridRenderCellParams<ApiKey>) =>
        formatTimestamp(params.row.createdDate),
    },
    {
      flex: 1,
      field: "expireDate",
      headerName: "Expiry Date",

      renderCell: (params: GridRenderCellParams<ApiKey>) =>
        formatTimestamp(params.row.expireDate),
    },
    {
      field: "status",
      headerName: "Status",

      disableColumnMenu: true,
      sortable: false,
      editable: false,
      width: 200,

      renderCell: (params: GridRenderCellParams<ApiKey>) => (
        <StatusGroup
          status={params.row.status as Status}
          expireDate={params.row.expireDate}
        />
      ),
    },
    {
      flex: 1.2,
      field: "id",
      headerName: "Actions",
      renderCell: (params: GridRenderCellParams<ApiKey>) => {
        return (
          <Box sx={{ display: "block", gap: 0 }}>
            <PermissionTooltipWrapper
              permissionKeys={[PermissionsKeys.appsUpdate]}
            >
              <IconButton
                color="info"
                onClick={async () => {
                  try {
                    history.push(
                      generatePath(Routes.developerApiKeysEditKey, {
                        keyName: params.id,
                      }),
                      {
                        keyName: params.id,
                      }
                    );
                  } catch (er) {}
                }}
              >
                <Pencil />
              </IconButton>
            </PermissionTooltipWrapper>
            <PermissionTooltipWrapper
              permissionKeys={[PermissionsKeys.appsDelete]}
            >
              <IconButton
                color="error"
                onClick={() => {
                  setSelectedKeyId(params.id as string);

                  setDeleteModalIsOpen(true);
                }}
              >
                <Trash />
              </IconButton>
            </PermissionTooltipWrapper>
            <ConfirmationModal
              isLoading={apiKeyDeleteMutation.isLoading}
              isError={apiKeyDeleteMutation.isError}
              open={deleteModalIsOpen}
              header={`Are you sure?`}
              body={`Deleting "${selectedKeyId}" API Key will permanently remove it from your account. This action cannot be undone.`}
              leftButtonText={"Yes, Delete API Key"}
              leftButtonColor="error"
              rightButtonText={"No, Keep API Key"}
              rightButtonColor="primary"
              handleLeftButton={() => {
                if (selectedKeyId) {
                  apiKeyDeleteMutation.mutate(selectedKeyId);
                }
              }}
              handleRightButton={() => {
                setDeleteModalIsOpen(false);
                setSelectedKeyId(null);
              }}
              handleClose={() => {
                setDeleteModalIsOpen(false);
                setSelectedKeyId(null);
              }}
            />
          </Box>
        );
      },
    },
  ];
};
const StatusGroup = (props: { status: Status; expireDate: number }) => {
  /**
   * Returns rendered chip(s) of the status of an API key
   */

  const color =
    props.status === "approved"
      ? "success"
      : props.status === "revoked"
      ? "error"
      : "info";

  let expired = false;

  if (props.expireDate !== -1 && props.expireDate <= Math.round(Date.now())) {
    expired = true;
  }

  return (
    <Box
      style={{
        display: "flex",
        gap: "5%",
        height: "100%",
        alignItems: "center",
      }}
    >
      {expired ? (
        <Chip label="Expired" color="error" variant="outlined" />
      ) : (
        <Chip
          label={toTitleCase(props.status)}
          color={color}
          variant="outlined"
        />
      )}
    </Box>
  );
};

export const columnsAdmin: GridColDef[] = [
  { flex: 1, field: "name", headerName: "Name" },
  {
    flex: 1,
    field: "createdDate",
    headerName: "Create Date",

    renderCell: (params: GridRenderCellParams) =>
      formatTimestamp(params.row.createdDate),
  },
  {
    flex: 1,
    field: "expireDate",
    headerName: "Expiry Date",

    renderCell: (params: GridRenderCellParams) =>
      formatTimestamp(params.row.expireDate),
  },
  {
    field: "status",
    headerName: "Status",

    disableColumnMenu: true,
    sortable: false,
    editable: false,
    width: 200,

    renderCell: (params: GridRenderCellParams) => (
      <StatusGroup
        status={params.row.status}
        expireDate={params.row.expireDate}
      />
    ),
  },
];
